import { __awaiter, __extends, __generator, __read, __spreadArray } from "tslib";
import { AsyncResult } from "./AsyncResult";
import { Optional } from "../Optional";
var Result = (function () {
    function Result() {
    }
    Result.fromThrowable = function (throwable) {
        try {
            return Success.of(throwable());
        }
        catch (e) {
            return Fail.of(e);
        }
    };
    Result.wrap = function (fn) {
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return Result.fromThrowable(function () { return fn.apply(void 0, __spreadArray([], __read(args), false)); });
        };
    };
    Result.fromPromise = function (promise) {
        return AsyncResult.fromPromise(promise);
    };
    Result.success = function (value) {
        return Success.of(value);
    };
    Result.fail = function (value) {
        return Fail.of(value);
    };
    Result.prototype.flatMap = function (mapSuccess, mapError) {
        return this.map(mapSuccess, function (err) {
            return mapError ? mapError(err).getOrThrowFailure() : err;
        }).map(function (s) { return s.getOrThrowFailure(); });
    };
    Result.prototype.flatMapAsync = function (mapSuccess, mapError) {
        var _this = this;
        return this.mapAsync(function (result) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4, mapSuccess(result).getOrThrowFailure()];
                    case 1: return [2, _a.sent()];
                }
            });
        }); }, function (err) { return __awaiter(_this, void 0, void 0, function () { var _a; return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!mapError) return [3, 2];
                    return [4, mapError(err).getOrThrowFailure()];
                case 1:
                    _a = _b.sent();
                    return [3, 3];
                case 2:
                    _a = err;
                    _b.label = 3;
                case 3: return [2, (_a)];
            }
        }); }); });
    };
    return Result;
}());
export { Result };
var Fail = (function (_super) {
    __extends(Fail, _super);
    function Fail(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Fail.of = function (value) {
        return new Fail(value);
    };
    Fail.prototype.mergeFailure = function (mapFailure) {
        return mapFailure(this.value);
    };
    Fail.prototype.get = function () {
        return this.value;
    };
    Fail.prototype.map = function (_, mapError) {
        try {
            return mapError
                ? new Fail(mapError(this.value))
                : new Fail(this.value);
        }
        catch (e) {
            return new Fail(e);
        }
    };
    Fail.prototype.tap = function (_, mapError) {
        return this.map(function (v) { return v; }, function (e) {
            if (mapError) {
                mapError(e);
            }
            return e;
        });
    };
    Fail.prototype.mapAsync = function (_, mapError) {
        if (mapError) {
            return AsyncResult.unwrapResult(Promise.resolve(mapError(this.value)).then(function (err) { return Fail.of(err); }));
        }
        else {
            return AsyncResult.unwrapResult(Promise.resolve(this));
        }
    };
    Fail.prototype.tapAsync = function (_, tapError) {
        var _this = this;
        if (tapError) {
            return AsyncResult.unwrapResult(tapError(this.value).then(function () { return Fail.of(_this.value); }));
        }
        else {
            return AsyncResult.unwrapResult(Promise.resolve(this));
        }
    };
    Fail.prototype.mapFailure = function (mapFailure) {
        return this.map(function (i) { return i; }, mapFailure);
    };
    Fail.prototype.isSuccess = function () {
        return false;
    };
    Fail.prototype.isFailure = function () {
        return true;
    };
    Fail.prototype.getOrThrowFailure = function () {
        if (this.value instanceof Error) {
            if (Error.captureStackTrace) {
                Error.captureStackTrace(this.value, this.getOrThrowFailure);
            }
            throw this.value;
        }
        else {
            throw new TypeError("Result threw a non error type: ".concat(this.value));
        }
    };
    Fail.prototype.recover = function (recover) {
        return Success.of(recover(this.value));
    };
    Fail.prototype.recoverAsync = function (mapError) {
        return AsyncResult.fromPromise(mapError(this.value));
    };
    Fail.prototype.getEither = function () {
        return this.value;
    };
    Fail.prototype.toOptionalFailure = function () {
        return Optional.of(this.value);
    };
    Fail.prototype.toOptional = function () {
        return Optional.empty();
    };
    return Fail;
}(Result));
export { Fail };
var Success = (function (_super) {
    __extends(Success, _super);
    function Success(value) {
        var _this = _super.call(this) || this;
        _this.value = value;
        return _this;
    }
    Success.of = function (value) {
        return new Success(value);
    };
    Success.prototype.mergeFailure = function () {
        return this.value;
    };
    Success.prototype.recover = function () {
        return this;
    };
    Success.prototype.recoverAsync = function () {
        return AsyncResult.success(this.value);
    };
    Success.prototype.get = function () {
        return this.value;
    };
    Success.prototype.tap = function (mapSuccess) {
        return this.map(function (v) {
            if (mapSuccess) {
                mapSuccess(v);
            }
            return v;
        });
    };
    Success.prototype.tapAsync = function (tapSuccess) {
        var _this = this;
        return AsyncResult.fromPromise(tapSuccess(this.value).then(function () { return _this.value; }));
    };
    Success.prototype.map = function (mapSuccess) {
        try {
            return new Success(mapSuccess(this.value));
        }
        catch (e) {
            return Result.fail(e);
        }
    };
    Success.prototype.mapAsync = function (mapSuccess) {
        return AsyncResult.fromPromise(mapSuccess(this.value));
    };
    Success.prototype.mapFailure = function () {
        return this;
    };
    Success.prototype.isSuccess = function () {
        return true;
    };
    Success.prototype.isFailure = function () {
        return false;
    };
    Success.prototype.getOrThrowFailure = function () {
        return this.value;
    };
    Success.prototype.getEither = function () {
        return this.value;
    };
    Success.prototype.toOptionalFailure = function () {
        return Optional.empty();
    };
    Success.prototype.toOptional = function () {
        return Optional.of(this.value);
    };
    return Success;
}(Result));
export { Success };
